import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { AuditEventDto, ClimateDto, FellowNote, MeetingDetail, SummaryDto, TranscriptResponse, TranscriptSegment } from '../../shared/api-types.js';
import type { AiProvider } from '../../shared/enums.js';
import { autoProcessWindowLabel } from '../../shared/fellow.js';
import {
  Banner,
  Button,
  Chip,
  FailureView,
  Icon,
  IconButton,
  RelativeTime,
  Spinner,
  StateView,
  StatusPill,
  TextField,
  browserTimeZone,
  formatCount,
  formatDateOnly,
  formatDateTime,
  formatList,
  formatTime,
  formatTimestamp,
  groupByDay,
  pluralise,
  timeZoneLabel,
} from '../ui/index.js';
import { activityEntryLabel, activityWindowLine, describeMeetingActivity, type ActivityExtent, type ActivityLine } from './lib/activity.js';
import { meetingClimateText } from './lib/climate.js';
import { parseMarkdownLite, type InlineNode, type MarkdownBlock } from './lib/markdown.js';
import type { LazyRequestState } from './lib/use-lazy-request.js';
import { fellowNoteItemStatus, summaryFailureKind, summaryProviderLabel, type SummaryState } from './lib/summary.js';
import {
  TRANSCRIPT_QUERY_MAX,
  TRANSCRIPT_QUERY_PARAM,
  foldText,
  highlightParts,
  normaliseTranscriptQuery,
  readTranscriptQuery,
  searchTranscript,
  searchWithoutTranscriptQuery,
  segmentLinkLabel,
  transcriptSearchTerms,
  type TextRange,
} from './lib/transcript-search.js';
import './pages.css';

// The record page's tab panels (06-modules §5.3 R3/R4). Each keeps its own four states, reserves
// its space while loading, and never renders model output as HTML.

// --- Markdown rendering ------------------------------------------------------------------------

function InlineNodes({ nodes }: { nodes: readonly InlineNode[] }) {
  return (
    <>
      {nodes.map((node, index) => {
        switch (node.kind) {
          case 'strong':
            return <strong key={index}><InlineNodes nodes={node.children} /></strong>;
          case 'em':
            return <em key={index}><InlineNodes nodes={node.children} /></em>;
          case 'code':
            return <code key={index}>{node.text}</code>;
          case 'text':
          default:
            return <span key={index}>{node.text}</span>;
        }
      })}
    </>
  );
}

/** Paragraphs, lists and emphasis from the Markdown-lite tree — React elements, never innerHTML. */
export function MarkdownBlocks({ blocks }: { blocks: readonly MarkdownBlock[] }) {
  return (
    <>
      {blocks.map((block, index) => {
        switch (block.kind) {
          case 'heading':
            return <p key={index}><strong><InlineNodes nodes={block.inlines} /></strong></p>;
          case 'list':
            return block.ordered ? (
              <ol key={index}>{block.items.map((item, itemIndex) => <li key={itemIndex}><InlineNodes nodes={item} /></li>)}</ol>
            ) : (
              <ul key={index}>{block.items.map((item, itemIndex) => <li key={itemIndex}><InlineNodes nodes={item} /></li>)}</ul>
            );
          case 'paragraph':
          default:
            return <p key={index}><InlineNodes nodes={block.inlines} /></p>;
        }
      })}
    </>
  );
}

// --- Summary tab -------------------------------------------------------------------------------

export interface SummaryTabProps {
  meeting: MeetingDetail;
  /** The state the page shows, which may be a locally requested regeneration ahead of the API. */
  state: SummaryState;
  aiProvider: AiProvider;
  /** Grant AND floor: whether this reader may (re)generate. */
  canGenerate: boolean;
  requesting: boolean;
  onGenerate: () => void;
  /** Imported outside the automatic window and nothing started yet (spec §10): offer to process it. */
  awaitingProcessing: boolean;
  processing: boolean;
  onProcess: () => void;
  /** True once polling has given up so the reader is told to check back rather than watch a spinner. */
  pollingExhausted: boolean;
  onCheckAgain: () => void;
}

export function SummaryTab({ meeting, state, aiProvider, canGenerate, requesting, onGenerate, awaitingProcessing, processing, onProcess, pollingExhausted, onCheckAgain }: SummaryTabProps) {
  const summary = meeting.summary;
  const blocks = useMemo(() => parseMarkdownLite(summary?.summaryMarkdown), [summary?.summaryMarkdown]);
  const notes = <FellowNotesDisclosure notes={meeting.fellowNotes} />;

  if (state === 'queued' || state === 'running') {
    return (
      <div className="stack">
        <div className="summary-pending" role="status" aria-live="polite">
          <Spinner size={20} label={state === 'queued' ? 'Summary pending' : 'Summary running'} />
          <div className="stack stack--1">
            <p><strong>{state === 'queued' ? 'Summary pending' : 'Summary running'}</strong></p>
            <p className="text-sm secondary">
              {state === 'queued' ? 'Waiting to start. ' : 'The transcript is being read now. '}
              This usually takes a minute or two; the page checks by itself and you can leave and come back.
              {summary?.isDemo || aiProvider === 'offline' ? ' The Demo summariser will generate it.' : ''}
            </p>
            {pollingExhausted ? (
              <p className="text-sm">
                It is taking longer than usual. <Button variant="link" size="sm" onClick={onCheckAgain}>Check again</Button>
              </p>
            ) : null}
          </div>
        </div>
        {summary && summary.status === 'ready' ? (
          <>
            <p className="text-sm muted">The previous summary stays until the new one is ready.</p>
            <ReadySummary summary={summary} blocks={blocks} climate={meeting.climate} />
          </>
        ) : null}
        {notes}
      </div>
    );
  }

  if (!summary || state === 'none') {
    if (meeting.transcriptStatus === 'empty') {
      return (
        <div className="stack">
          <StateView
            kind="empty"
            icon="file-text"
            title="No transcript to summarise"
            description="Fellow held no transcript for this recording, so Calis cannot generate a summary or to-dos for it."
            minHeight="10rem"
          />
          {notes}
        </div>
      );
    }
    if (awaitingProcessing) {
      // What processing does depends on the account: the paid summary, or Fellow's own items as
      // to-dos — and the reason for the latter is named, floor and grant apart (05-client §5.4).
      const effect = canGenerate
        ? 'Processing it generates a summary, key points, decisions and a to-do list from the transcript.'
        : aiProvider === 'off'
          ? 'Processing it imports Fellow’s own action items from its notes as to-dos; this installation has no AI provider configured, so no summary is generated.'
          : 'Processing it imports Fellow’s own action items from its notes as to-dos; summaries are generated for accounts that include the AI summaries capability.';
      return (
        <div className="stack">
          <StateView
            kind="empty"
            icon="clock"
            title="Not processed yet"
            description={`This meeting was more than ${autoProcessWindowLabel()} old when it was imported, so Calis left it for you to decide. ${effect}`}
            action={<Button variant="secondary" icon="play" onClick={onProcess} loading={processing}>Process meeting</Button>}
            minHeight="10rem"
          />
          {notes}
        </div>
      );
    }
    return (
      <div className="stack">
        <StateView
          kind={aiProvider === 'off' ? 'not-configured' : 'empty'}
          icon="sparkles"
          title={aiProvider === 'off' ? 'AI summaries are not switched on' : 'No summary yet'}
          description={aiProvider === 'off'
            ? 'This installation has no AI provider configured, so Calis cannot generate summaries or to-dos. Nothing here is failing; Fellow’s own notes are below if it made any.'
            : canGenerate
              ? 'Generate one to get a summary, key points, decisions and a to-do list drawn from the transcript.'
              : 'Summaries are generated for accounts that include the AI summaries capability. Ask an administrator if you need it; Fellow’s own notes are below if it made any.'}
          action={canGenerate ? <Button variant="secondary" icon="sparkles" onClick={onGenerate} loading={requesting}>Generate summary</Button> : undefined}
          minHeight="10rem"
        />
        {notes}
      </div>
    );
  }

  if (summary.status === 'failed') {
    const kind = summaryFailureKind(summary);
    return (
      <div className="stack">
        <StateView
          kind={kind === 'not-configured' ? 'not-configured' : 'error'}
          title={kind === 'not-configured' ? 'AI summaries are not switched on' : 'The summary could not be generated'}
          description={summary.errorMessage ?? 'The last attempt failed.'}
          action={canGenerate && kind !== 'not-configured' ? <Button variant="secondary" icon="refresh" onClick={onGenerate} loading={requesting}>Try again</Button> : undefined}
          minHeight="10rem"
        />
        <p className="text-xs muted">
          Last attempt <RelativeTime value={summary.completedAt ?? summary.createdAt} /> by {summaryProviderLabel(summary)}.
        </p>
        {notes}
      </div>
    );
  }

  return (
    <div className="stack">
      <ReadySummary summary={summary} blocks={blocks} climate={meeting.climate} />
      {notes}
    </div>
  );
}

function ReadySummary({ summary, blocks, climate }: { summary: SummaryDto; blocks: readonly MarkdownBlock[]; climate: ClimateDto | null }) {
  return (
    <>
      <p className="summary-provenance">
        {summary.isDemo ? <Chip tone="warning" icon="bolt" title="Generated by the demo summariser, not by an AI model">Demo summariser</Chip> : null}
        <span>
          {summary.isDemo ? 'Generated' : `Generated by ${summaryProviderLabel(summary)}`} <RelativeTime value={summary.completedAt ?? summary.createdAt} />
        </span>
      </p>
      {/* The Climate row (ADR 0019; design §2.5): the climate this summary answered, the person's own, and that it is
          counted, never named, into the organisation pulse — present only when the server sent one (it sends one only
          while executive intelligence is on), so nothing is derived about the person that they cannot see. A reading,
          worded with its qualifier, never a pill (05-content §6.1). */}
      {climate ? <p className="text-sm secondary" data-climate="meeting">{meetingClimateText(climate)}</p> : null}
      {blocks.length > 0 ? (
        <div className="summary-body">
          <MarkdownBlocks blocks={blocks} />
        </div>
      ) : (
        <p className="muted">The summary text is empty.</p>
      )}
      <div className="summary-columns">
        <section aria-labelledby="summary-key-points" className="stack stack--2">
          <h3 id="summary-key-points">Key points</h3>
          {summary.keyPoints.length > 0 ? (
            <ul className="summary-list">{summary.keyPoints.map((point, index) => <li key={index}>{point}</li>)}</ul>
          ) : <p className="muted text-sm">None recorded.</p>}
        </section>
        <section aria-labelledby="summary-decisions" className="stack stack--2">
          <h3 id="summary-decisions">Decisions</h3>
          {summary.decisions.length > 0 ? (
            <ul className="summary-list">{summary.decisions.map((decision, index) => <li key={index}>{decision}</li>)}</ul>
          ) : <p className="muted text-sm">None recorded.</p>}
        </section>
      </div>
    </>
  );
}

/**
 * Fellow's own AI notes, behind a disclosure (03-components §13) — detail, never the summary itself. Exported for the
 * rendering test (test/server/client-pages/fellow-notes-headings.test.ts); the summary tab composes it.
 */
export function FellowNotesDisclosure({ notes }: { notes: readonly FellowNote[] }) {
  if (notes.length === 0) return null;
  const sectionCount = notes.reduce((count, note) => count + note.sections.length, 0);
  // Why the section heading's level is derived (01-foundations §7.3: no skipped levels; Phase 1c finding D26): the tab
  // panel is headed by an <h2>, and a note's own title (<h3>) is drawn only when there are several notes. With one note
  // its sections were <h4>s straight under the <h2>; they take the level beneath whatever heads them.
  const SectionHeading = notes.length > 1 ? 'h4' : 'h3';
  return (
    <details className="disclosure">
      <summary>
        <Icon name="chevron-down" size={16} />
        Fellow&rsquo;s own notes ({pluralise(sectionCount, { one: '# section', other: '# sections' })})
      </summary>
      <div className="disclosure__body stack stack--3">
        <p className="text-sm muted">Written by Fellow&rsquo;s AI when the recording was made; shown as Fellow sent them.</p>
        {notes.map((note, noteIndex) => (
          <article key={note.id ?? noteIndex} className="fellow-note stack stack--2">
            {notes.length > 1 ? <h3 className="text-md">{note.title}</h3> : null}
            {note.sections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="stack stack--1">
                <SectionHeading>{section.title}</SectionHeading>
                {section.text ? <p className="summary-body">{section.text}</p> : null}
                {section.items.length > 0 ? (
                  <ul className="summary-list">
                    {section.items.map((item, itemIndex) => (
                      <li key={item.id ?? itemIndex}>
                        {item.text}
                        {/* Why each fact a phrase of its own beside the item, and Fellow's status in Calis's words: the
                            names are a list the locale joins (05-content §8), and "Done" as Fellow stored it is on
                            Completed's Not-list (§6; the owner's ruling Q9, "Completed in Fellow"; Phase 1c R14). */}
                        {item.assignees.length > 0 ? <span className="muted"> — {formatList(item.assignees)}</span> : null}
                        {item.dueDate ? <span className="muted"> · {`Due ${formatDateOnly(item.dueDate)}`}</span> : null}
                        {fellowNoteItemStatus(item.status) ? <span className="muted"> · {fellowNoteItemStatus(item.status)}</span> : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {!section.text && section.items.length === 0 ? <p className="muted text-sm">Empty section.</p> : null}
              </div>
            ))}
          </article>
        ))}
      </div>
    </details>
  );
}

// --- Transcript tab ----------------------------------------------------------------------------

const SEGMENT_HASH = /^#t-(\d+)$/u;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The search field: what is being typed, committed to the address after a pause or on Enter. It is its own
 * component so a keystroke re-renders the field alone, never a transcript of a thousand segments.
 *
 * Why the writes it has sent are remembered: the router applies an address change in a transition, so letters
 * typed while one lands are newer than it. The address coming back with a value this field sent is therefore not
 * copied into the field — that would erase them — and only an address changed from elsewhere (Back, a link, the
 * empty state's "Clear search") is. Why on every arrival (the address's key) and not on a changed query: two writes
 * can land as one, or as no change at all, and a write left on the list would hide a later Back to the same words.
 * The field is never reset to the trimmed address either: that would eat the space a reader paused after.
 */
function TranscriptSearchField({ query, addressKey, onSearch, inputRef }: { query: string; addressKey: string; onSearch: (next: string) => void; inputRef: RefObject<HTMLInputElement | null> }) {
  const [draft, setDraft] = useState(query);
  const sent = useRef<string[]>([]);
  const queryRef = useRef(query);
  queryRef.current = query;

  useEffect(() => {
    const own = sent.current.indexOf(query);
    if (own !== -1) {
      sent.current = sent.current.slice(own + 1);
      return;
    }
    sent.current = [];
    setDraft((current) => (normaliseTranscriptQuery(current) === query ? current : query));
  }, [query, addressKey]);

  const commit = useCallback((next: string) => {
    const normalised = normaliseTranscriptQuery(next);
    const latest = sent.current.length > 0 ? sent.current[sent.current.length - 1] : queryRef.current;
    if (normalised === latest) return;
    sent.current = [...sent.current, normalised];
    onSearch(normalised);
  }, [onSearch]);

  useEffect(() => {
    const handle = window.setTimeout(() => commit(draft), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [draft, commit]);

  return (
    <form
      role="search"
      aria-label="Search the transcript"
      className="filter-bar__search transcript-search"
      onSubmit={(event) => {
        event.preventDefault();
        commit(draft);
      }}
    >
      <Icon name="search" size={16} className="filter-bar__search-icon" />
      <TextField
        ref={inputRef}
        label="Search what was said"
        type="search"
        inputMode="search"
        enterKeyHint="search"
        autoComplete="off"
        spellCheck={false}
        maxLength={TRANSCRIPT_QUERY_MAX}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        className="filter-bar__search-field"
      />
      {draft !== '' ? (
        <IconButton
          icon="x"
          label="Clear search"
          size="sm"
          className="filter-bar__search-clear"
          onClick={() => {
            setDraft('');
            commit('');
            inputRef.current?.focus();
          }}
        />
      ) : null}
    </form>
  );
}

/** "4 segments of 35 match “budget”." — whole sentences, one per plural form (05-content §8). */
function matchSentence(matching: number, total: number, query: string): string {
  if (matching === 0) return `No segment matches “${query}”.`;
  return pluralise(matching, {
    one: `# segment of ${formatCount(total)} matches “${query}”. Select its timestamp to read it in the full transcript.`,
    other: `# segments of ${formatCount(total)} match “${query}”. Select a timestamp to read one in the full transcript.`,
  });
}

export function TranscriptTab({ request, transcriptStatus }: { request: LazyRequestState<TranscriptResponse>; transcriptStatus: MeetingDetail['transcriptStatus'] }) {
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = useRef(location);
  locationRef.current = location;
  const target = useMemo(() => {
    const match = SEGMENT_HASH.exec(location.hash);
    return match ? Number(match[1]) : null;
  }, [location.hash]);
  const segments = useMemo(() => request.data?.segments ?? [], [request.data]);
  const query = useMemo(() => readTranscriptQuery(new URLSearchParams(location.search)), [location.search]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Why replace: a search typed letter by letter is one view, not a history entry per pause. A new search drops the
  // segment link (#t-) — a new question, and the old target would only be reported as hidden — while clearing the
  // search keeps it, so the segment a link was for comes back into view.
  const search = useCallback((next: string) => {
    const current = locationRef.current;
    const params = new URLSearchParams(current.search);
    if (next === '') params.delete(TRANSCRIPT_QUERY_PARAM);
    else params.set(TRANSCRIPT_QUERY_PARAM, next);
    const rest = params.toString();
    navigate({ pathname: current.pathname, search: rest === '' ? '' : `?${rest}`, hash: next === '' ? current.hash : '' }, { replace: true });
  }, [navigate]);

  // Folded once per transcript, not once per keystroke.
  const foldedTexts = useMemo(() => segments.map((segment) => foldText(segment.text).text), [segments]);
  const terms = useMemo(() => transcriptSearchTerms(query), [query]);
  const hits = useMemo(() => (terms.length === 0 ? null : searchTranscript(segments, foldedTexts, terms)), [segments, foldedTexts, terms]);

  const ready = request.status === 'ready';
  const targetExists = target !== null && segments.some((segment) => segment.index === target);
  const targetShown = targetExists && (hits === null || hits.some((hit) => hit.segment.index === target));
  const missing = ready && target !== null && !targetExists ? target : null;
  const hiddenBySearch = ready && targetExists && !targetShown ? target : null;

  // Arriving by #t-<index>: bring the segment into view and identify it, or say it is not there, or that the
  // search hides it (06-modules §5.5 — never a silent landing at the top). Why on every arrival (the location's
  // key) and not only on a new target: a result opened in the full transcript keeps its #t-, in a longer list.
  useEffect(() => {
    if (target === null || !ready || !targetShown) return;
    const frame = requestAnimationFrame(() => {
      const element = document.getElementById(`t-${target}`);
      if (!element) return;
      element.scrollIntoView({ block: 'center', behavior: 'auto' });
      element.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [target, ready, targetShown, location.key]);

  if (transcriptStatus === 'empty') {
    return (
      <StateView
        kind="empty"
        icon="file-text"
        title="No transcript"
        description="Fellow held no transcript for this recording."
        minHeight="10rem"
      />
    );
  }
  if (request.status === 'idle' || request.status === 'loading') {
    return <StateView kind="loading" label="Loading the transcript" minHeight="16rem" />;
  }
  if (request.status === 'error' && request.error) {
    return <FailureView failure={request.error} what="the transcript" onRetry={request.reload} minHeight="10rem" />;
  }
  if (segments.length === 0) {
    return <StateView kind="empty" icon="file-text" title="Transcript empty" description="Fellow reported a transcript but sent no segments for it." minHeight="10rem" />;
  }

  const clearSearch = () => {
    search('');
    inputRef.current?.focus();
  };
  // A segment in the full transcript: what a result's timestamp opens, so it can be read in context. Why a new
  // history entry here, unlike a timestamp in the full transcript: Back then returns to the results.
  const inContext = (index: number) => ({ search: searchWithoutTranscriptQuery(location.search), hash: `#t-${index}` });
  const language = request.data?.languageCode ? languageName(request.data.languageCode) : null;
  const shown: readonly { segment: TranscriptSegment; ranges: readonly TextRange[] | null }[] = hits ?? segments.map((segment) => ({ segment, ranges: null }));

  return (
    <div className="stack stack--3">
      <TranscriptSearchField query={query} addressKey={location.key} onSearch={search} inputRef={inputRef} />
      {missing !== null ? (
        <Banner tone="info" role="status">
          The transcript segment you followed a link to (number {formatCount(missing + 1)}) is not in this transcript; the recording may have been updated in Fellow since.
        </Banner>
      ) : null}
      {hiddenBySearch !== null ? (
        <Banner tone="info" role="status" className="transcript-search__echo">
          The transcript segment you followed a link to (number {formatCount(hiddenBySearch + 1)}) does not match the search “{query}”.{' '}
          <Link to={inContext(hiddenBySearch)}>Show it in the full transcript</Link>
        </Banner>
      ) : null}
      <p className="text-sm secondary transcript-search__echo" role="status">
        {hits === null
          ? <>{pluralise(segments.length, { one: '# segment', other: '# segments' })}{language ? ` · ${language}` : ''} · in the order spoken. Each timestamp is a link you can send.</>
          : matchSentence(hits.length, segments.length, query)}
      </p>
      {hits !== null && hits.length === 0 ? (
        <StateView
          kind="empty"
          icon="search"
          title="No matches"
          description={terms.length > 1 ? 'Every word you search for has to be in the same segment. Try fewer words, or different ones.' : 'Try a different word, or part of one.'}
          action={<Button variant="secondary" onClick={clearSearch}>Clear search</Button>}
          minHeight="10rem"
        />
      ) : (
        <ol className="transcript" role="list" aria-label={hits === null ? 'Transcript, in the order spoken' : 'Segments that match the search, in the order spoken'}>
          {shown.map(({ segment, ranges }) => {
            const highlighted = target === segment.index;
            return (
              <li
                key={segment.index}
                id={`t-${segment.index}`}
                tabIndex={-1}
                className={['transcript__segment', 'record-anchor', highlighted ? 'is-highlighted' : ''].filter(Boolean).join(' ')}
                aria-current={highlighted ? 'true' : undefined}
              >
                {ranges === null ? (
                  <Link to={{ search: location.search, hash: `#t-${segment.index}` }} className="transcript__time num hit-area" aria-label={segmentLinkLabel(segment.index, segment.start, false)} replace>
                    {segment.start !== null ? `[${formatTimestamp(segment.start)}]` : `#${segment.index + 1}`}
                  </Link>
                ) : (
                  <Link to={inContext(segment.index)} className="transcript__time num hit-area" aria-label={segmentLinkLabel(segment.index, segment.start, true)}>
                    {segment.start !== null ? `[${formatTimestamp(segment.start)}]` : `#${segment.index + 1}`}
                  </Link>
                )}
                <div className="stack stack--1">
                  {segment.speaker ? <span className="transcript__speaker">{segment.speaker}</span> : <span className="transcript__speaker muted">Unknown speaker</span>}
                  <p className="transcript__text">{ranges === null ? segment.text : <SegmentText text={segment.text} ranges={ranges} />}</p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

/** A segment's words with what the search found marked. */
export function SegmentText({ text, ranges }: { text: string; ranges: readonly TextRange[] }) {
  return (
    <>
      {highlightParts(text, ranges).map((part, position) => (part.match ? <mark key={position} className="transcript__match">{part.text}</mark> : <Fragment key={position}>{part.text}</Fragment>))}
    </>
  );
}

/** "en-GB" → "British English"; falls back to the code where the runtime cannot name it. */
export function languageName(code: string): string {
  try {
    const names = new Intl.DisplayNames(['en'], { type: 'language' });
    return names.of(code) ?? code;
  } catch {
    return code;
  }
}

// --- Activity tab ------------------------------------------------------------------------------

const ENTRY_HASH = /^#a-([A-Za-z0-9_-]+)$/u;

/** What any record's activity route answers (`MeetingActivityResponse`, `ProjectActivityResponse`): the retained rows and the window. */
export interface ActivityFeedResponse<T extends AuditEventDto = AuditEventDto> {
  items: T[];
  retentionDays: number;
}

export interface ActivityFeedProps<T extends AuditEventDto = AuditEventDto> {
  /**
   * The sentence for one row. The meeting's by default; the project record page (design §7.3 R4) passes
   * `describeProjectActivity`, so one feed serves every record page and the entry anatomy is written once.
   */
  describe?: (event: T) => ActivityLine;
  /** The record in the reader's words — "this meeting", "this project" — for the error state and the closing line. */
  subject?: string;
  /**
   * The feed's own account of its extent (04-data §6.6, §6.7): the retention window's when absent, as the meeting's is; a
   * record whose feed knows more passes its own (the project's: `projectActivityExtent`, the re-audit's D31).
   */
  extent?: (data: ActivityFeedResponse<T>, capped: boolean) => ActivityExtent;
  /** A sentence under the window line saying what the feed holds, where that is narrower than everything about the record. */
  grain?: string;
  /** The day headings' level: 3 beneath the tab's own heading, 4 where the feed sits under a heading of its own (§6.8). */
  dayHeadingLevel?: 3 | 4;
}

export interface ActivityTabProps<T extends AuditEventDto = AuditEventDto> extends ActivityFeedProps<T> {
  request: LazyRequestState<ActivityFeedResponse<T>>;
}

/** The retention window as the whole of a feed's extent: the meeting's, and the default. */
function windowExtent(subject: string) {
  return (data: ActivityFeedResponse<AuditEventDto>, capped: boolean): ActivityExtent => ({
    empty: { title: pluralise(data.retentionDays, { one: 'No activity in the last day', other: 'No activity in the last # days' }), description: 'Older entries are not kept.' },
    closing: capped ? 'Older entries within the window exist beyond the 200 shown.' : `This is the whole retained history of ${subject}.`,
  });
}

/** How many skeleton entries a loading feed draws: enough to hold the region open at its usual first-screen height. */
export const ACTIVITY_SKELETON_ENTRIES = 4;

/**
 * A feed while it loads (04-data §6.7, "Loading — skeleton entries at the real entry height. The region MUST NOT collapse
 * to zero"; the Calis conformity re-audit's R24). Why the real entry's markup with skeleton bars in place of its words,
 * not a spinner in a box of guessed height: an entry's height is its mark, one line of statement and one of time, padded
 * as an entry is, so a skeleton built of the same parts is the same height by construction — and the feed that lands
 * replaces it without moving what is beneath it. Each part waits the 200ms every skeleton waits before it shows
 * (`.activity__entry--skeleton`, `.skeleton`; 03-components §1), and keeps its room meanwhile. A feed that states its
 * grain says it here too, in words: the sentence is the feed's own, known before its rows, and holding its line keeps the
 * entries from moving down by it when they land.
 */
export function ActivitySkeleton({ label = 'Loading activity', grain }: { label?: string; grain?: string }) {
  return (
    <div className="activity activity--loading" aria-busy="true">
      <span className="visually-hidden" role="status">{label}</span>
      <p className="text-sm secondary" aria-hidden="true"><span className="skeleton activity__skeleton-window" /></p>
      {grain ? <p className="text-sm secondary">{grain}</p> : null}
      <section className="activity__day" aria-hidden="true">
        <p className="activity__day-heading"><span className="skeleton activity__skeleton-day" /></p>
        <ol className="activity__list">
          {Array.from({ length: ACTIVITY_SKELETON_ENTRIES }, (_, index) => (
            <li key={index} className="activity__entry activity__entry--skeleton">
              <span className="activity__mark" />
              <div className="stack stack--1">
                <p className="activity__statement"><span className="skeleton activity__skeleton-statement" /></p>
                {/* Why the bar sits in a span: the time line is a flex row, whose item would take the bar's own height;
                    in a span it sits on a line box, which stands at the line's height as the real time does. */}
                <p className="activity__when"><span><span className="skeleton activity__skeleton-when" /></span></p>
              </div>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

export function ActivityTab<T extends AuditEventDto = AuditEventDto>({ request, ...feed }: ActivityTabProps<T>) {
  const subject = feed.subject ?? 'this meeting';
  if (request.status === 'idle' || request.status === 'loading') return <ActivitySkeleton grain={feed.grain} />;
  if (request.status === 'error' && request.error) {
    return <FailureView failure={request.error} what={`${subject}’s activity`} onRetry={request.reload} minHeight="10rem" />;
  }
  const data = request.data;
  if (!data) return null;
  return <ActivityFeed data={data} {...feed} />;
}

/** A loaded feed: the window line, the days and their entries, and the line that says how much history this is. */
export function ActivityFeed<T extends AuditEventDto = AuditEventDto>({ data, describe, subject = 'this meeting', extent, grain, dayHeadingLevel = 3 }: ActivityFeedProps<T> & { data: ActivityFeedResponse<T> }) {
  const location = useLocation();
  const timeZone = browserTimeZone();
  const zoneLabel = timeZoneLabel(timeZone);
  const target = useMemo(() => ENTRY_HASH.exec(location.hash)?.[1] ?? null, [location.hash]);
  const [missing, setMissing] = useState<string | null>(null);
  const sentence = describe ?? (describeMeetingActivity as (event: T) => ActivityLine);

  useEffect(() => {
    if (!target) return;
    const element = document.getElementById(`a-${target}`);
    if (!element) {
      setMissing(target);
      return;
    }
    setMissing(null);
    const frame = requestAnimationFrame(() => {
      element.scrollIntoView({ block: 'center', behavior: 'auto' });
      element.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [target]);

  const items = data.items;
  const groups = groupByDay(items, (item) => item.createdAt, timeZone);
  const capped = items.length >= 200;
  const windowLine = activityWindowLine(data.retentionDays, capped, zoneLabel);
  const told = (extent ?? (windowExtent(subject) as (data: ActivityFeedResponse<T>, capped: boolean) => ActivityExtent))(data, capped);
  const DayHeading = dayHeadingLevel === 4 ? 'h4' : 'h3';

  if (items.length === 0) {
    return <StateView kind="empty" icon="clock" title={told.empty.title} description={told.empty.description} minHeight="10rem" />;
  }

  let position = 0;
  return (
    <div className="activity">
      <p className="text-sm secondary">{windowLine}</p>
      {grain ? <p className="text-sm secondary">{grain}</p> : null}
      {missing ? <Banner tone="info" role="status">The activity entry you followed a link to is not in the retained history any more.</Banner> : null}
      {groups.map((group) => (
        <section key={group.day} className="activity__day" aria-labelledby={`activity-day-${group.day}`}>
          <DayHeading id={`activity-day-${group.day}`} className="activity__day-heading">{group.label}</DayHeading>
          <ol className="activity__list" aria-label={`Activity on ${group.label}`}>
            {group.items.map((event) => {
              position += 1;
              const line = sentence(event);
              const absolute = formatDateTime(event.createdAt, { timeZone });
              const highlighted = target === event.id;
              return (
                <li
                  key={event.id}
                  id={`a-${event.id}`}
                  tabIndex={-1}
                  className={['activity__entry', 'record-anchor', highlighted ? 'is-highlighted' : ''].filter(Boolean).join(' ')}
                  aria-current={highlighted ? 'true' : undefined}
                  aria-label={activityEntryLabel({ position, total: items.length, line, when: absolute })}
                >
                  <span className={['activity__mark', line.system ? 'activity__mark--system' : ''].filter(Boolean).join(' ')} aria-hidden="true">
                    <Icon name={line.system ? 'bolt' : 'user'} size={16} />
                  </span>
                  <div className="stack stack--1">
                    <p className="activity__statement">
                      <strong>{line.actorName}</strong> {line.action}
                      {line.outcome ? <> <StatusPill tone={line.outcome.tone}>{line.outcome.label}</StatusPill></> : null}
                    </p>
                    <p className="activity__when">
                      <time dateTime={event.createdAt} title={absolute}>{formatTime(event.createdAt, { timeZone })}</time>
                      <span aria-hidden="true">·</span>
                      <RelativeTime value={event.createdAt} />
                      {line.surfacePhrase ? (
                        <>
                          <span aria-hidden="true">·</span>
                          <span>{line.surfacePhrase}</span>
                        </>
                      ) : null}
                    </p>
                  </div>
                  {line.details.length > 0 ? (
                    <details className="activity__details">
                      <summary>Details</summary>
                      <dl>
                        {line.details.map((detail) => (
                          <div key={detail.label} className="activity__detail">
                            <dt>{detail.label}</dt>
                            <dd>{detail.value}</dd>
                          </div>
                        ))}
                      </dl>
                    </details>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </section>
      ))}
      <p className="text-xs muted">{told.closing}</p>
    </div>
  );
}
